import type { Store } from '@reduxjs/toolkit';
import { useAppStateStore } from '@src/providers/AppStateProvider';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { useExecutionReports } from '@src/providers/ExecutionReportsContext';
import { useOrders } from '@src/providers/OrdersProvider';
import type { FullOrderRequest, ModifyOrderRequest } from '@src/providers/orders.types';
import {
  type ErrorSubscriptionResponse,
  ExecTypeEnum,
  type ExecutionReport,
  isOrderPending,
  type IWebSocketClient,
  logger,
  NEW_ORDER_SINGLE,
  NotificationVariants,
  type Order,
  ORDER_MODIFY_REQUEST,
  OrdStatusEnum,
  type SubscriptionResponse,
  type ToastProps,
  useServiceInstantiator,
  useSocketClient,
} from '@talos/kyoko';
import { noop } from 'lodash-es';
import type { Observable, Subscription } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { DatadogTradingAction, trackOrderAction } from '../../../utils/datadogTradingActions';

export const EXEC_REPORT_RESPONSE_TIMEOUT_MS = 10_000;

interface InternalRequestState {
  timeoutId?: ReturnType<typeof setTimeout>;
  showedTimeoutError: boolean;
  quiet: boolean;
  subscriptions: Subscription[];
}

export const OrderRequestStatus = {
  /** The order request has been published and is waiting for an execution report or order update. */
  Loading: 'loading',
  /** The websocket publication failed before the request could be applied by the backend. */
  PublicationError: 'publicationError',
  /** The backend responded, but the request was rejected, cancelled, or otherwise not applied. */
  NotApplied: 'notApplied',
  /** The backend confirmed the request was applied successfully. */
  Success: 'success',
} as const;
export type OrderRequestStatus = (typeof OrderRequestStatus)[keyof typeof OrderRequestStatus];

export type OrderRequestState =
  | {
      clOrdID: string;
      status: typeof OrderRequestStatus.Loading;
    }
  | {
      clOrdID: string;
      status: typeof OrderRequestStatus.PublicationError;
      error: string;
    }
  | {
      clOrdID: string;
      status: typeof OrderRequestStatus.NotApplied;
      order: ExecutionReport | Order;
    }
  | {
      clOrdID: string;
      status: typeof OrderRequestStatus.Success;
      order: ExecutionReport | Order;
    };

type OnOrderRequestStateChange = (clOrdID: string, requestState: OrderRequestState) => void;

interface OrderServiceRequestOptions {
  quiet?: boolean;
}

/**
 * Handles websocket communication around Principal Orders
 * - Publishes typed order placement and modification requests
 * - Triggers state changes and showing errors when hearing acks on orders
 */
export class OrderService {
  private requestStateByClOrdID = new Map<string, InternalRequestState>();

  constructor(
    private store: Store<AppState>,
    private webSocketClient: IWebSocketClient<unknown>,
    private executionReportSubscription: Observable<SubscriptionResponse<ExecutionReport>>,
    private recentOrdersSubscription: Observable<Order[]>,
    private addToast: (props: ToastProps) => void,
    private onOrderRequestStateChange: OnOrderRequestStateChange = noop
  ) {}

  public requestOrder(request: FullOrderRequest, options: OrderServiceRequestOptions = {}): void {
    this.debug('NewOrderSingle', request);
    this.startRequestState(request, options);
    trackOrderAction(DatadogTradingAction.Submit, { clOrdID: request.ClOrdID });

    const cleanup = this.webSocketClient.registerPublication(
      {
        type: NEW_ORDER_SINGLE,
        data: [request],
      },
      err => this.handleNewOrderSingleError(err, request.ClOrdID)
    );

    // stop listening for errors 10 seconds after publication
    setTimeout(() => cleanup(), 10_000);
  }

  public updateOrder(request: ModifyOrderRequest, options: OrderServiceRequestOptions = {}): void {
    if (!request.OrderID) {
      logger.error(new Error('Missing required fields for order modification'), {
        extra: { request, requestOrderID: request.OrderID },
      });
      return;
    }
    this.debug('OrderCancelReplace', request);
    this.startRequestState(request, options);
    trackOrderAction(DatadogTradingAction.Modify, { clOrdID: request.ClOrdID, orderID: request.OrderID });

    const cleanup = this.webSocketClient.registerPublication(
      {
        type: ORDER_MODIFY_REQUEST,
        data: [request],
      },
      err => this.handleNewOrderSingleError(err, request.ClOrdID)
    );

    // stop listening for errors 10 seconds after publication
    setTimeout(() => cleanup(), 10_000);
  }

  private handleNewOrderSingleError(error: ErrorSubscriptionResponse, clOrdID: string) {
    const quiet = this.requestStateByClOrdID.get(clOrdID)?.quiet === true;

    logger.error(new Error(`NewOrderSingle publication returned an error in OrderService`), {
      extra: { error },
    });

    this.onOrderRequestStateChange(clOrdID, {
      clOrdID,
      status: OrderRequestStatus.PublicationError,
      error: error.error?.msg ?? 'Unknown publication error',
    });

    if (!quiet) {
      this.addToast({
        id: uuid(),
        text: `Error: ${error.error?.msg}`,
        variant: NotificationVariants.Negative,
        timeout: 5000,
        dismissable: true,
      });
    }

    this.clearRequestState(clOrdID);
  }

  private startRequestState(request: FullOrderRequest | ModifyOrderRequest, options: OrderServiceRequestOptions) {
    this.onOrderRequestStateChange(request.ClOrdID, { clOrdID: request.ClOrdID, status: OrderRequestStatus.Loading });

    const requestState: InternalRequestState = {
      showedTimeoutError: false,
      quiet: options.quiet === true,
      subscriptions: [],
    };

    requestState.timeoutId = setTimeout(() => {
      logger.error(new Error(`Order Submission took longer than expected (${EXEC_REPORT_RESPONSE_TIMEOUT_MS}ms)`), {
        extra: { request },
      });

      if (!requestState.quiet) {
        this.addToast({
          id: uuid(),
          text: 'The request is taking longer than expected',
          variant: NotificationVariants.Warning,
          timeout: 5000,
          dismissable: true,
        });

        // Instead of just clearing the timeout-tracking map entry for this ClOrdID, we keep it around in case
        // we get a (very) late ER for this ClOrdID. If we do, we track that this happened, for posterity in dd.
        requestState.showedTimeoutError = true;
      }
      requestState.timeoutId = undefined;
    }, EXEC_REPORT_RESPONSE_TIMEOUT_MS);

    requestState.subscriptions = this.observeExecReports(request);
    this.requestStateByClOrdID.set(request.ClOrdID, requestState);
  }

  private observeExecReports({ ClOrdID, OrderID }: { ClOrdID: string; OrderID?: string }): Subscription[] {
    // We observe the exec reports sub as if our request ends in an error (like modifying an order that went terminal)
    // we will only get an exec report for the ClOrdID and no order will be created.
    const execSub = this.executionReportSubscription.subscribe(this.processExecReportOrOrderForClOrdId(ClOrdID));

    // We also observe the orders sub as if we have a disconnect while the order is in flight we will not get the missing
    // exec reports when that subscription recovers
    const orderSub = this.recentOrdersSubscription.subscribe(this.processExecReportOrOrderForClOrdId(ClOrdID, OrderID));
    return [execSub, orderSub];
  }

  private processExecReportOrOrderForClOrdId(clOrdID: string, orderID?: string) {
    return (execReportResponseOrOrderArray: SubscriptionResponse<ExecutionReport, string> | Order[]) => {
      let execReportOrOrder: Order | ExecutionReport | undefined = undefined;
      if (Array.isArray(execReportResponseOrOrderArray)) {
        execReportOrOrder = execReportResponseOrOrderArray.find(order => order.ClOrdID === clOrdID);
        this.debug('orderReceived', execReportOrOrder);
      } else {
        execReportOrOrder = execReportResponseOrOrderArray.data.findLast(report => report.ClOrdID === clOrdID);
        this.debug('executionReportReceived', execReportOrOrder);
      }

      if (!execReportOrOrder) {
        // This is not the order/execReport you are looking for
        return;
      }
      const quiet = this.requestStateByClOrdID.get(clOrdID)?.quiet === true;

      if (
        execReportOrOrder.OrdStatus === OrdStatusEnum.Rejected ||
        execReportOrOrder.OrdStatus === OrdStatusEnum.Canceled ||
        // ExecType can be ReplaceRejected (indicating a failure) despite OrdStatus not reflecting a terminal status
        execReportOrOrder.ExecType === ExecTypeEnum.ReplaceRejected
      ) {
        // We failed, emit a notApplied state and then stop listening, nothing more to do
        if (!quiet) {
          const verb = execReportOrOrder.OrdStatus === OrdStatusEnum.Rejected ? 'rejected' : 'cancelled';
          this.addToast({
            text: execReportOrOrder.Text || `Order ${verb} with unknown reason. Please contact Talos support.`,
            variant: NotificationVariants.Warning,
            timeout: 5000,
          });
        }
        this.onOrderRequestStateChange(clOrdID, {
          clOrdID,
          status: OrderRequestStatus.NotApplied,
          order: execReportOrOrder,
        });

        this.clearRequestState(clOrdID);
        return;
      }

      if (isOrderPending(execReportOrOrder.OrdStatus)) {
        // Pending updates can still be followed by a rejection, so keep the request state alive until a final update.
        return;
      }

      // Else we received some kind of successful result
      this.onOrderRequestStateChange(clOrdID, {
        clOrdID,
        status: OrderRequestStatus.Success,
        order: execReportOrOrder,
      });

      this.clearRequestState(clOrdID);
    };
  }

  private unsubClOrdID(clOrdID: string) {
    const requestState = this.requestStateByClOrdID.get(clOrdID);
    if (!requestState) {
      return;
    }

    requestState.subscriptions.forEach(subscription => subscription.unsubscribe());
    requestState.subscriptions = [];
  }

  // Called on resolution of a request: either successful ER came in matching the ClOrdID, or the publication errored.
  private clearRequestState(clOrdID: string) {
    const requestState = this.requestStateByClOrdID.get(clOrdID);
    if (!requestState) {
      return;
    }

    if (requestState.timeoutId) {
      clearTimeout(requestState.timeoutId);
    }

    if (requestState.showedTimeoutError) {
      // Log if this request was very slow. This is just for posterity, think post-mortem logging.
      logger.info('Recovered after showing timeout error to user', {
        extra: { clOrdId: clOrdID },
      });
    }

    this.unsubClOrdID(clOrdID);
    this.requestStateByClOrdID.delete(clOrdID);
  }

  private debug(key: string, value: any) {
    // dispatch an action so it's logged and can be debugged in the redux debugger but isn't actually handled by reducer and does not affect state in any ways
    this.store.dispatch({ type: `order/debug/${key}`, payload: value });
  }

  public destroy(): void {
    this.requestStateByClOrdID.forEach(requestState => {
      if (requestState.timeoutId) {
        clearTimeout(requestState.timeoutId);
      }
      requestState.subscriptions.forEach(subscription => subscription.unsubscribe());
      requestState.subscriptions = [];
    });
    this.requestStateByClOrdID.clear();
  }
}

interface UseOrderServiceParams {
  addToast?: (props: ToastProps) => void;
  onOrderRequestStateChange?: OnOrderRequestStateChange;
}

/** Basic OrderService hook without any coupling to the order slice. Just collects the streams needed to instantiate an instance. */
export function useOrderService({ addToast, onOrderRequestStateChange }: UseOrderServiceParams = {}): OrderService {
  const store = useAppStateStore();
  const webSocketClient = useSocketClient();
  const { executionReportSub } = useExecutionReports();
  const { recentOrders } = useOrders();

  return useServiceInstantiator(
    () =>
      new OrderService(
        store,
        webSocketClient,
        executionReportSub,
        recentOrders,
        addToast ?? noop,
        onOrderRequestStateChange
      ),
    [store, webSocketClient, executionReportSub, recentOrders, addToast, onOrderRequestStateChange]
  );
}
